/**
 * ministryLoadBalancer.js
 * Synthesizes session fatigue + ministry pastoral load → gentle pastoral guidance.
 *
 * Sources:
 *   - humanRhythm → session intensity / fatigue
 *   - districtStateEngine → pastoral load (0–1) from visit/event/prayer counts
 *   - workflow state → active workflow count
 *
 * Outputs a pastoral-language load assessment.
 * Never uses productivity or corporate language.
 */

import { detectSessionRhythm }  from "../kevia-runtime/humanRhythm";
import { getPastoralLoad }       from "../kevia-data/districtStateEngine";
import { getActiveWorkflows }    from "../kevia-workflows/workflowState";

const OVERLOAD_WORKFLOW_THRESHOLD = 5;

/**
 * @typedef {"low"|"medium"|"high"|"critical"} LoadLevel
 */

const BALANCE_NOTES = {
  high: [
    "Esta semana parece bastante cargada. Quizá convendría enfocarse solo en los seguimientos más urgentes.",
    "Hay muchas cosas activas. Podría ser útil definir qué es prioritario hoy.",
  ],
  critical: [
    "Hay señales de sobrecarga ministerial. Convendría priorizar y descansar.",
    "Demasiadas cosas simultáneas pueden dificultar el cuidado pastoral profundo.",
  ],
};

function pickNote(load, seed = 0) {
  const bank = BALANCE_NOTES[load];
  if (!bank) return null;
  return bank[seed % bank.length];
}

/**
 * Assess current ministry load and return pastoral guidance.
 *
 * @returns {{
 *   load:               LoadLevel,
 *   sessionIntensity:   "low"|"medium"|"high",
 *   activeWorkflows:    number,
 *   shouldSuggestFocus: boolean,
 *   balanceNote:        string | null,
 * }}
 */
export function assessMinistryLoad() {
  const rhythm         = detectSessionRhythm();
  const pastoralLoad   = getPastoralLoad(); // 0–1 from district state
  const activeWfs      = getActiveWorkflows().length;

  // Combine session rhythm + pastoral load + workflow count
  const workflowScore  = Math.min(1, activeWfs / OVERLOAD_WORKFLOW_THRESHOLD);
  const compositeScore = (rhythm.fatigueScore * 0.35) + (pastoralLoad * 0.45) + (workflowScore * 0.20);

  const load =
    compositeScore >= 0.80 ? "critical" :
    compositeScore >= 0.55 ? "high"     :
    compositeScore >= 0.30 ? "medium"   :
    "low";

  const shouldSuggestFocus = load === "high" || load === "critical";
  const seed = Math.round(compositeScore * 10);

  return {
    load,
    sessionIntensity: rhythm.intensity,
    activeWorkflows:  activeWfs,
    shouldSuggestFocus,
    balanceNote:      shouldSuggestFocus ? pickNote(load === "critical" ? "critical" : "high", seed) : null,
  };
}
